import React from "react";
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	DragEndEvent,
} from "@dnd-kit/core";
import {
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { FormField, FormSection, FieldType } from "../../types/formTypes";
import { SortableItem, DragHandle } from "../ui/sortable-item";
import { GripVertical, Plus, Type } from "lucide-react";
import FormFieldEditor from "../form-field-editor";
import { Button } from "../../../../components/ui/button";
import { TooltipWrapper } from "../../../../components/ui/tooltip-wrapper";

interface SectionFieldsProps {
	section: FormSection;
	onUpdateField: (fieldId: string, updates: Partial<FormField>) => void;
	onDeleteField: (fieldId: string) => void;
	onDuplicateField: (fieldId: string) => void;
	onDragEnd: (result: { fromIndex: number; toIndex: number }) => void;
	onAddFieldAt: (index: number, type?: FieldType) => void;
}

const SectionFields: React.FC<SectionFieldsProps> = ({
	section,
	onUpdateField,
	onDeleteField,
	onDuplicateField,
	onDragEnd,
	onAddFieldAt,
}) => {
	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8, // Increased distance to avoid accidental drags
				tolerance: 5, // Add tolerance for slight movements
				delay: 150, // Short delay before activating drag
			},
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;

		if (over && active.id !== over.id) {
			const activeIndex = section.fields.findIndex(
				(field) => field.id === active.id
			);
			const overIndex = section.fields.findIndex(
				(field) => field.id === over.id
			);

			if (activeIndex !== -1 && overIndex !== -1) {
				onDragEnd({
					fromIndex: activeIndex,
					toIndex: overIndex,
				});
			}
		}
	};

	return (
		<div>
			{section.fields.length > 0 ? (
				<DndContext
					sensors={sensors}
					collisionDetection={closestCenter}
					onDragEnd={handleDragEnd}
				>
					<SortableContext
						items={section.fields.map((field) => field.id)}
						strategy={verticalListSortingStrategy}
					>
						<div className="space-y-0">
							{section.fields.map((field, index) => (
								<React.Fragment key={field.id}>
									<SortableItem
										id={field.id}
										handleSelector=".field-drag-handle"
									>
										<div className="border rounded-lg p-4 bg-gray-50 hover:shadow-sm transition-shadow flex items-top gap-2">
											<DragHandle>
												<div className="py-0.5 rounded-md hover:bg-gray-200 mt-1.5">
													<GripVertical className="h-5 w-5 text-gray-400" />
												</div>
											</DragHandle>

											<div className="flex-1">
												<FormFieldEditor
													field={field}
													onUpdateField={(updates) =>
														onUpdateField(
															field.id,
															updates
														)
													}
													onDeleteField={() =>
														onDeleteField(field.id)
													}
													onDuplicateField={() =>
														onDuplicateField(field.id)
													}
												/>
											</div>
										</div>
									</SortableItem>

									{/* Add buttons after each field except the last one*/}
									{index !== section.fields.length - 1 && (
										<div className="flex justify-center items-center gap-2 py-0.5 group">
											<TooltipWrapper title="Feld hier einfügen" duration={150}>
												<div>
												<Button
													variant="ghost"
													size="icon"
													onClick={() => onAddFieldAt(index + 1)}
													className="h-6 w-6 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
													aria-label="Feld hier einfügen"
												>
													<Plus className="h-4 w-4" />
												</Button>
												</div>
											</TooltipWrapper>
											<TooltipWrapper title="Titel hier einfügen" duration={150}>
												<Button
													variant="ghost"
													size="icon"
													onClick={() => onAddFieldAt(index + 1, 'title')}
													className="h-6 w-6 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
													aria-label="Titel hier einfügen"
												>
													<Type className="h-4 w-4" />
												</Button>
											</TooltipWrapper>
										</div>
									)}
								</React.Fragment>
							))}
						</div>
					</SortableContext>
				</DndContext>
			) : (
				<div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed">
					<p className="text-gray-500">Keine Felder hinzugefügt</p>
					<p className="text-gray-400 text-sm mt-1">
						Fügen Sie ein Feld mit den untenstehenden Buttons hinzu
					</p>
				</div>
			)}
		</div>
	);
};

export default SectionFields;
